import React, { useState, useEffect } from "react";
import api from "../../services/api";
import { format } from "date-fns";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  FiDollarSign,
  FiUsers,
  FiTrendingUp,
  FiRefreshCw,
} from "react-icons/fi";

function AdminDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [realTime, setRealTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("30d");

  useEffect(() => {
    fetchDashboard();
    fetchRealTimeMetrics();

    // Refresh real-time metrics every 30 seconds
    const interval = setInterval(fetchRealTimeMetrics, 30000);
    return () => clearInterval(interval);
  }, [dateRange]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/dashboard/admin?range=${dateRange}`);
      setDashboard(response.data.data);
    } catch (err) {
      console.error("Failed to load admin dashboard", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRealTimeMetrics = async () => {
    try {
      const response = await api.get("/dashboard/realtime");
      setRealTime(response.data.data);
    } catch (err) {
      console.error("Failed to load real-time metrics", err);
    }
  };

  const formatCurrency = (amount, currency = "usd") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0,
    }).format(amount / 100);
  };

  const COLORS = ["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6"];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  const mrrTotal =
    dashboard?.mrr?.reduce((sum, m) => sum + parseInt(m.mrr || 0), 0) || 0;

  return (
    <div>
      <div className="page-header flex-between">
        <div>
          <h1 className="page-title">Admin Dashboard</h1>
          <p className="page-subtitle">Payment analytics and metrics</p>
        </div>
        <select
          className="form-input"
          style={{ width: "auto" }}
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="1y">Last year</option>
        </select>
      </div>

      {/* Real-time Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">MRR</div>
          <div className="stat-value">{formatCurrency(mrrTotal)}</div>
          <div className="stat-change positive">
            <FiTrendingUp /> Monthly Recurring Revenue
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active Subscriptions</div>
          <div className="stat-value">{realTime?.subscriptions?.active || 0}</div>
          {realTime?.subscriptions?.endingSoon > 0 && (
            <div className="stat-change" style={{ color: "var(--warning)" }}>
              ⚠️ {realTime.subscriptions.endingSoon} renewing within 7 days
            </div>
          )}
        </div>
        <div className="stat-card">
          <div className="stat-label">Revenue (24h)</div>
          <div className="stat-value">
            {formatCurrency(realTime?.last24Hours?.revenue || 0)}
          </div>
          <div className="stat-change">
            {realTime?.last24Hours?.transactions || 0} transactions
            {realTime?.last24Hours?.failed > 0 && (
              <span style={{ color: "var(--error)", marginLeft: "8px" }}>
                · {realTime.last24Hours.failed} failed
              </span>
            )}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Trial Conversion</div>
          <div className="stat-value">
            {dashboard?.trialConversions?.conversion_rate || 0}%
          </div>
          <div className="stat-change">
            {dashboard?.trialConversions?.converted || 0} of{" "}
            {dashboard?.trialConversions?.total_trials || 0}
            {realTime?.trials?.endingSoon > 0 && (
              <span style={{ color: "var(--warning)", marginLeft: "8px" }}>
                · {realTime.trials.endingSoon} trials ending soon
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-2">
        {/* Revenue Chart */}
        <div className="card">
          <h2 className="card-title">Revenue Over Time</h2>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={dashboard?.revenue || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis
                dataKey="date"
                tickFormatter={(date) => format(new Date(date), "MMM d")}
                stroke="#94a3b8"
              />
              <YAxis
                tickFormatter={(value) => `$${value / 100}`}
                stroke="#94a3b8"
              />
              <Tooltip
                formatter={(value) => formatCurrency(value)}
                labelFormatter={(date) => format(new Date(date), "MMM d, yyyy")}
                contentStyle={{
                  background: "#1e293b",
                  border: "1px solid #475569",
                }}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#6366f1"
                fill="rgba(99, 102, 241, 0.3)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Subscription Status */}
        <div className="card">
          <h2 className="card-title">Subscription Status</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={dashboard?.subscriptionStats || []}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="count"
                nameKey="status"
                label={({ status, count }) => `${status}: ${count}`}
              >
                {(dashboard?.subscriptionStats || []).map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: "#1e293b",
                  border: "1px solid #475569",
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-2 mt-4" style={{ marginTop: "24px" }}>
        {/* Provider Breakdown */}
        <div className="card">
          <h2 className="card-title">Payment Providers</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dashboard?.providerBreakdown || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="provider" stroke="#94a3b8" />
              <YAxis
                tickFormatter={(value) => `$${value / 100}`}
                stroke="#94a3b8"
              />
              <Tooltip
                formatter={(value) => formatCurrency(value)}
                contentStyle={{
                  background: "#1e293b",
                  border: "1px solid #475569",
                }}
              />
              <Bar
                dataKey="total_amount"
                fill="#6366f1"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top Products */}
        <div className="card">
          <h2 className="card-title">Top Products</h2>
          <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Subscribers</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {(dashboard?.topProducts || []).slice(0, 5).map((product) => (
                <tr key={product.id}>
                  <td>{product.name}</td>
                  <td>{product.subscriber_count || 0}</td>
                  <td>{formatCurrency(product.total_revenue || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {/* Churn Analysis */}
      <div className="card mt-4" style={{ marginTop: "24px" }}>
        <h2 className="card-title">Churn Analysis</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={dashboard?.churnAnalysis || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis
              dataKey="month"
              tickFormatter={(date) => format(new Date(date), "MMM yyyy")}
              stroke="#94a3b8"
            />
            <YAxis stroke="#94a3b8" />
            <Tooltip
              labelFormatter={(date) => format(new Date(date), "MMMM yyyy")}
              contentStyle={{
                background: "#1e293b",
                border: "1px solid #475569",
              }}
            />
            <Legend />
            <Bar
              dataKey="new_subscriptions"
              name="New"
              fill="#22c55e"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey="churned_subscriptions"
              name="Churned"
              fill="#ef4444"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* New Customers */}
      {dashboard?.newCustomers?.length > 0 && (
        <div className="card mt-4" style={{ marginTop: "24px" }}>
          <h2 className="card-title">New Paying Customers</h2>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={dashboard.newCustomers}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis
                dataKey="date"
                tickFormatter={(date) => format(new Date(date), "MMM d")}
                stroke="#94a3b8"
              />
              <YAxis allowDecimals={false} stroke="#94a3b8" />
              <Tooltip
                formatter={(value) => [value, "New customers"]}
                labelFormatter={(date) => format(new Date(date), "MMM d, yyyy")}
                contentStyle={{
                  background: "#1e293b",
                  border: "1px solid #475569",
                }}
              />
              <Bar
                dataKey="new_customers"
                name="New Customers"
                fill="#6366f1"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
